import { Heart, Shield, ArrowRight, CheckCircle, TrendingUp, Lock, Clock, Landmark, HandCoins, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";

export const LifeInsuranceAnnuitiesPage = () => {
  const lifeInsuranceProducts = [
    {
      icon: Clock,
      title: "Term Life Insurance",
      bgColor: "bg-blue-50",
      description: "Affordable coverage for a specific period, providing financial protection when your family needs it most",
      features: [
        "Coverage for 10, 20, or 30 years",
        "Lower premiums than permanent life insurance",
        "Convertible to permanent coverage",
        "Ideal for income replacement and debt coverage",
      ],
    },
    {
      icon: Shield,
      title: "Whole Life Insurance",
      bgColor: "bg-slate-100",
      description: "Lifelong protection with guaranteed cash value growth and fixed premiums that never increase",
      features: [
        "Lifetime coverage guaranteed",
        "Cash value accumulation over time",
        "Fixed premiums that never change",
        "Dividend potential with participating policies",
      ],
    },
    {
      icon: TrendingUp,
      title: "Universal Life Insurance",
      bgColor: "bg-sky-50",
      description: "Flexible permanent coverage that lets you adjust premiums and death benefits as your needs change",
      features: [
        "Adjustable premiums and death benefits",
        "Cash value with competitive interest rates",
        "Indexed options tied to market performance",
        "Flexibility to adapt to life changes",
      ],
    },
    {
      icon: Heart,
      title: "Final Expense Insurance",
      bgColor: "bg-indigo-50",
      description: "Simplified coverage designed to cover funeral costs, medical bills, and end-of-life expenses",
      features: [
        "Simplified underwriting process",
        "Covers funeral and burial costs",
        "No medical exam required in most cases",
        "Affordable premiums for seniors",
      ],
    },
  ];

  const annuityProducts = [
    {
      icon: Lock,
      title: "Fixed Annuities",
      bgColor: "bg-blue-50",
      description: "A safe, predictable retirement income stream with guaranteed interest rates and principal protection",
      features: [
        "Guaranteed minimum interest rate",
        "Principal protection from market losses",
        "Tax-deferred growth on earnings",
        "Predictable income in retirement",
        "Options for lifetime income payments",
      ],
    },
    {
      icon: HandCoins,
      title: "Indexed Annuities",
      bgColor: "bg-sky-50",
      description: "The best of both worlds — growth linked to market indexes with downside protection for your principal",
      features: [
        "Returns linked to market indexes (S&P 500, etc.)",
        "Floor protection against market losses",
        "Tax-deferred accumulation",
        "Guaranteed minimum interest rate",
        "Flexible payout and withdrawal options",
      ],
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary to-blue-700 text-white section-padding overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1511895426328-dc8714191300?w=1600&q=80')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary/95 to-blue-700/90"></div>
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <ShieldCheck className="h-20 w-20 mx-auto mb-6 text-blue-100" />
            <h1 data-testid="page-title" className="text-4xl md:text-5xl font-bold mb-6">Life Insurance & Annuities</h1>
            <p className="text-xl text-blue-100 mb-8">
              Secure your family's future and build a reliable retirement income with our comprehensive life insurance and annuity solutions
            </p>
            <Link to="/contact">
              <Button data-testid="hero-cta-btn" size="lg" variant="secondary" className="bg-white text-primary hover:bg-gray-100 font-semibold px-8">
                Get Your Free Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Annuity Products */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 data-testid="annuities-heading" className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Annuity Solutions
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Build a dependable retirement income with annuity products designed to grow your savings and provide lifetime payments
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {annuityProducts.map((product, index) => (
              <Card key={index} data-testid={`annuity-product-card-${index}`} className={`card-hover border-2 hover:border-primary ${product.bgColor}`}>
                <CardHeader>
                  <div className="bg-primary/10 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                    <product.icon className="h-7 w-7 text-primary" />
                  </div>
                  <CardTitle className="text-xl">{product.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-6">{product.description}</p>
                  <ul className="space-y-3">
                    {product.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Life Insurance Products */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 data-testid="life-insurance-heading" className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Life Insurance Products
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Protect the people who depend on you with the right life insurance policy tailored to your needs and budget
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {lifeInsuranceProducts.map((product, index) => (
              <Card key={index} data-testid={`life-product-card-${index}`} className={`card-hover border-2 hover:border-primary ${product.bgColor}`}>
                <CardHeader>
                  <div className="bg-primary/10 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                    <product.icon className="h-7 w-7 text-primary" />
                  </div>
                  <CardTitle className="text-2xl">{product.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-6">{product.description}</p>
                  <ul className="space-y-3">
                    {product.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Visual Section */}
      <section className="section-padding bg-gradient-to-b from-white to-gray-50">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-foreground">
            Planning for Every Stage of Life
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {/* Family Protection Visual */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
              <img
                src="https://images.unsplash.com/photo-1536640712-4d4c36ff0e4e?w=800&q=80"
                alt="Happy family together"
                className="w-full h-[400px] object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent flex items-end">
                <div className="p-8 text-white">
                  <Heart className="h-12 w-12 mb-4" />
                  <h3 className="text-3xl font-bold mb-2">Life Insurance</h3>
                  <p className="text-gray-200 text-lg">Protect your family's financial future</p>
                </div>
              </div>
            </div>

            {/* Retirement Visual */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
              <img
                src="https://images.unsplash.com/photo-1473186578172-c141e6798cf4?w=800&q=80"
                alt="Couple enjoying retirement"
                className="w-full h-[400px] object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent flex items-end">
                <div className="p-8 text-white">
                  <Landmark className="h-12 w-12 mb-4" />
                  <h3 className="text-3xl font-bold mb-2">Annuities</h3>
                  <p className="text-gray-200 text-lg">Guaranteed income for your retirement years</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl p-8 md:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                  Why Work With Us?
                </h2>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-lg mb-1 text-foreground">Access to Top-Rated Carriers</h3>
                      <p className="text-muted-foreground">
                        As an independent agency, we represent multiple highly rated insurance companies to find the best fit for you
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-lg mb-1 text-foreground">Personalized Planning</h3>
                      <p className="text-muted-foreground">
                        We take time to understand your goals — whether it's protecting your family, saving for retirement, or leaving a legacy
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-lg mb-1 text-foreground">Unbiased Recommendations</h3>
                      <p className="text-muted-foreground">
                        We're not tied to a single company — we shop the market to recommend the policy that truly serves your interests
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-lg mb-1 text-foreground">Ongoing Support</h3>
                      <p className="text-muted-foreground">
                        From policy selection through claims and beneficiary changes, we're here for you at every step
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h3 className="text-2xl font-bold mb-4 text-center text-foreground">Ready to Plan Your Future?</h3>
                <p className="text-muted-foreground text-center mb-6">
                  Get a personalized life insurance or annuity quote and discover the right coverage for your family and retirement
                </p>
                <Link to="/contact">
                  <Button data-testid="bottom-cta-btn" size="lg" className="w-full font-semibold">
                    Request Your Free Quote
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <div className="mt-6 text-center text-sm text-muted-foreground">
                  <p>Serving MN, WI, IA, ND, SD, TN, and AZ</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
